import type { SeasonDeepContent } from "./types";

export const winterDeepContent: SeasonDeepContent = {
  COOL: {
    intro:
      "Cool Winter — sometimes called Clear Winter — sits at the icy edge of the Winter family, right where Winter's cool undertone starts to soften toward Summer's gentler contrast. Skin, hair, and eyes share a distinctly blue or neutral undertone with no warmth creeping in, but the overall value runs a touch lighter and the chroma a shade less saturated than True Winter's maximum brightness. Think snow light rather than midnight: high clarity, real contrast between features, but with an icy, almost translucent quality instead of Deep Winter's density. This is what separates a Cool Winter color palette from its neighbors — it wants clean, cool brights and true icy pastels, never anything muddy or warm. Reds read blue-based, blues stay crisp, and neutrals lean charcoal or cool grey rather than black-on-black. If you're deciding between Cool Winter and True Winter, the tell is usually softness: Cool Winter can wear an icy lavender or frost blue that would wash out a True Winter, while True Winter needs the extra saturation Cool Winter doesn't.",
    selfCheck: {
      skin: [
        "Cool or neutral undertone with a blue, pink, or rosy cast — never golden or peachy",
        "Skin has a clear, somewhat translucent quality rather than deep richness",
        "Looks best next to icy or bright cool colors, sallow next to warm or muted ones",
        "May burn or turn pink in the sun rather than tanning golden",
      ],
      hair: [
        "Cool-based hair color — ash brown, cool black, silver-grey, or ash blonde",
        "No golden, red, or warm caramel tones in the natural color",
        "Contrast between hair and skin is present but not stark, unlike True or Deep Winter",
        "Greys tend to come in silvery rather than yellowing",
      ],
      eyes: [
        "Clear, cool eye color — icy blue, cool grey, clear hazel, or dark cool brown",
        "Often has a bright or crystalline quality, sometimes with visible flecks",
        "Whites of the eyes read bright, not creamy",
        "No warm amber or golden ring around the iris",
      ],
      overall: [
        "Clear, cool, and bright rather than deep or muted",
        "Looks polished and awake in icy brights, tired in earthy warm tones",
        "Contrast is present but softer than True or Deep Winter",
        "Metallics read best in silver, white gold, or platinum — never yellow gold",
      ],
    },
    avoidDetails: [
      {
        name: "Warm Beige",
        hex: "#F5DEB3",
        reason: "Its yellow undertone flattens Cool Winter's clear, cool complexion into sallowness.",
        swapFor: "Cool White",
        swapHex: "#F0F8FF",
      },
      {
        name: "Camel",
        hex: "#C19A6B",
        reason: "Too warm and muted for a palette built on clarity and cool undertones.",
        swapFor: "Pewter",
        swapHex: "#899499",
      },
      {
        name: "Golden Brown",
        hex: "#996515",
        reason: "The golden cast fights Cool Winter's blue-based undertone and reads muddy against clear skin.",
        swapFor: "Charcoal",
        swapHex: "#36454F",
      },
      {
        name: "Terracotta",
        hex: "#E2725B",
        reason: "An orange-warm red that dulls Cool Winter's natural clarity instead of enhancing it.",
        swapFor: "Berry",
        swapHex: "#990066",
      },
      {
        name: "Warm Gold",
        hex: "#FFD700",
        reason: "Too warm and heavy a yellow for a palette that wants icy, cool brightness.",
        swapFor: "Ice Blue",
        swapHex: "#A5F2F3",
      },
      {
        name: "Rust Orange",
        hex: "#B7410E",
        reason: "A warm, earthy tone that has no place in Cool Winter's blue-and-jewel-toned range.",
        swapFor: "Sapphire",
        swapHex: "#0F52BA",
      },
    ],
    comparisons: [
      {
        vsLabel: "True Winter",
        vsHref: "/color-palette/winter/true",
        rows: [
          { attribute: "Undertone", thisSeason: "Cool, slightly softer", otherSeason: "Cool, maximum clarity" },
          { attribute: "Depth", thisSeason: "Light to medium, icy", otherSeason: "High contrast, vivid" },
          { attribute: "Best pink", thisSeason: "Icy pink", otherSeason: "Hot pink / fuchsia", thisHex: "#FFDEE9", otherHex: "#FF69B4" },
          {
            attribute: "Best blue/red",
            thisSeason: "Ice blue, berry",
            otherSeason: "Electric blue, ruby",
            thisHex: "#A5F2F3",
            otherHex: "#0055FF",
          },
          {
            attribute: "Metals",
            thisSeason: "Silver, platinum",
            otherSeason: "Silver, bright white gold",
            thisHex: "#C0C0C0",
            otherHex: "#E8E8E8",
          },
        ],
      },
      {
        vsLabel: "Bright Spring",
        vsHref: "/color-palette/spring/bright",
        rows: [
          { attribute: "Undertone", thisSeason: "Cool", otherSeason: "Warm" },
          { attribute: "Depth", thisSeason: "Light to medium, clear", otherSeason: "Light to medium, clear" },
          { attribute: "Best pink", thisSeason: "Icy pink", otherSeason: "Coral pink", thisHex: "#FFDEE9", otherHex: "#F88379" },
          {
            attribute: "Best blue/red",
            thisSeason: "Ice blue, berry",
            otherSeason: "Turquoise, warm red",
            thisHex: "#A5F2F3",
            otherHex: "#40E0D0",
          },
          {
            attribute: "Metals",
            thisSeason: "Silver, platinum",
            otherSeason: "Gold, bright gold",
            thisHex: "#C0C0C0",
            otherHex: "#FFD700",
          },
        ],
      },
    ],
    capsule: [
      { item: "Wool coat", color: "Charcoal", hex: "#36454F" },
      { item: "Blazer", color: "Cool Slate", hex: "#708090" },
      { item: "Blouse", color: "Ice Blue", hex: "#A5F2F3" },
      { item: "Trousers", color: "Charcoal", hex: "#36454F" },
      { item: "Sweater", color: "Periwinkle", hex: "#CCCCFF" },
      { item: "Dress", color: "Berry", hex: "#990066" },
      { item: "Cardigan", color: "Pewter", hex: "#899499" },
      { item: "Shoes", color: "Cool White", hex: "#F0F8FF" },
      { item: "Bag", color: "Sapphire", hex: "#0F52BA" },
      { item: "Denim", color: "Lapis", hex: "#26619C" },
    ],
    celebrities: [
      {
        name: "Kim Kardashian",
        why: "Deep near-black hair and clear, cool-toned skin give her the icy contrast Cool Winter thrives on.",
        imageUrl:
          "https://upload.wikimedia.org/wikipedia/commons/thumb/d/df/Kim_Kardashian_West_2014.jpg/330px-Kim_Kardashian_West_2014.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Kim_Kardashian",
      },
      {
        name: "Megan Fox",
        why: "Cool, dark hair against fair, clear skin and bright eyes reads as classic icy Winter contrast.",
        imageUrl:
          "https://upload.wikimedia.org/wikipedia/commons/thumb/a/a8/Megan_Fox_-_Jennifer%27s_Body.jpg/330px-Megan_Fox_-_Jennifer%27s_Body.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Megan_Fox",
      },
      {
        name: "Sandra Bullock",
        why: "Cool brunette hair and clear cool-neutral skin give her the crisp, contained brightness of Cool Winter.",
        imageUrl:
          "https://upload.wikimedia.org/wikipedia/commons/thumb/7/74/Sandra_Bullock_at_The_Egyptian_Theatre_2024.jpg/330px-Sandra_Bullock_at_The_Egyptian_Theatre_2024.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Sandra_Bullock",
      },
      {
        name: "Lucy Liu",
        why: "Cool black hair and clear, cool undertoned skin give her the icy clarity that defines this subseason.",
        imageUrl:
          "https://upload.wikimedia.org/wikipedia/commons/thumb/e/e0/Lucy_Liu_Rosemead-13_%28cropped%29.jpg/330px-Lucy_Liu_Rosemead-13_%28cropped%29.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Lucy_Liu",
      },
      {
        name: "Courteney Cox",
        why: "Cool brunette coloring and bright, clear eyes give her the crisp, icy quality of Cool Winter.",
        imageUrl:
          "https://upload.wikimedia.org/wikipedia/commons/thumb/8/83/Courteney_Cox_%2710_PaleyFest.jpg/330px-Courteney_Cox_%2710_PaleyFest.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Courteney_Cox",
      },
      {
        name: "Catherine Zeta-Jones",
        why: "Deep cool hair paired with fair, clear skin and bright eyes is a textbook Cool Winter combination.",
        imageUrl:
          "https://upload.wikimedia.org/wikipedia/commons/thumb/0/08/Catherine_Zeta-Jones_2025.png/330px-Catherine_Zeta-Jones_2025.png",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Catherine_Zeta-Jones",
      },
    ],
    extraFaqs: [
      {
        question: "Is Cool Winter the same as Clear Winter?",
        answer:
          "On this site, yes — Cool Winter and Clear Winter refer to the same subseason. Some systems call it Light Winter too. Whatever the label, it means the icier, slightly softer end of the Winter palette rather than the maximum-contrast True Winter.",
      },
      {
        question: "How is Cool Winter different from Bright Spring?",
        answer:
          "Both are clear and light-to-medium in depth, but the undertone flips. Cool Winter is cool with blue or pink undertones; Bright Spring is warm with golden undertones. If coral and peach flatter you more than icy pink and berry, you're likely Bright Spring, not Cool Winter.",
      },
      {
        question: "What jewelry metals suit Cool Winter?",
        answer:
          "Silver, platinum, and white gold. These cool metals match your undertone and keep the icy clarity intact. Yellow gold tends to look heavy and slightly off against Cool Winter skin — save it for warmer palettes.",
      },
      {
        question: "What hair colors work best for Cool Winter?",
        answer:
          "Ash brown, cool black, ash blonde, and silvery greys all work well. Avoid warm caramel, golden blonde, or copper tones, which fight your cool undertone. If you color your hair, ask for ash or cool-based formulas.",
      },
      {
        question: "Can Cool Winter wear black?",
        answer:
          "Yes, though it's not always your single best neutral — charcoal and cool slate can be even more flattering because they echo your slightly softer depth. Black still works beautifully, especially paired with an icy accent color near the face.",
      },
      {
        question: "How do I confirm I'm Cool Winter and not True Winter?",
        answer:
          "Hold an icy pastel like ice blue or periwinkle next to your face, then a saturated bright like electric blue or fuchsia. If the icy pastel makes you glow and the bright feels like slightly too much, you're Cool Winter. If the bright is the one that lights you up, look at True Winter instead.",
      },
    ],
    vibe: {
      skinTones: ["#F5E3DC", "#E3C6BC", "#C9A398", "#8B6459"],
      hairTones: ["#1C2841", "#3A3030", "#E8E4E8", "#2A2020"],
      eyeColors: ["#7FA8C9", "#4A4A52", "#3A2E28", "#8090A0"],
    },
    hairToneSwatches: [
      { name: "Blue-Black", hexes: ["#0A0F1A", "#1C2841", "#4A5A78"] },
      { name: "Cool Dark Brown", hexes: ["#211D1D", "#3A3030", "#6A5C5C"] },
      { name: "Icy Platinum", hexes: ["#B0AAB0", "#E8E4E8", "#FAF8FA"] },
      { name: "Cool Espresso", hexes: ["#161010", "#2A2020", "#524040"] },
      { name: "Silver Grey", hexes: ["#787880", "#A8A8B0", "#D8D8DE"] },
      { name: "Cool Ash Brown", hexes: ["#2E2828", "#5A5050", "#8A8080"] },
      { name: "Jet Black", hexes: ["#000000", "#1C1C1C", "#3A3A3A"] },
    ],
    metals: [
      { name: "Silver", hexes: ["#8A8F94", "#C0C0C0", "#EDEDED"] },
      { name: "Platinum", hexes: ["#ABA9A6", "#E5E4E2", "#FAFAF9"] },
      { name: "White Gold", hexes: ["#AEAAA5", "#E8E4E1", "#FBFAF8"] },
    ],
  },
  TRUE: {
    intro:
      "True Winter sits at the most concentrated point of the Winter family: maximum coolness, maximum contrast, maximum clarity. Where Cool Winter softens toward icy pastels and Deep Winter shifts toward density, True Winter holds the center — pure cool undertones with no warmth at all, high value contrast between skin, hair, and eyes, and colors that read fully saturated rather than muted or pale. This is the season built for a true black-and-white palette accented with electric jewel tones: think a snowy mountain range under a hard blue sky rather than the softer haze of dusk. A true winter color palette leans on cool reds, sapphire blues, and emerald greens precisely because anything warm or dusty gets swallowed by the intensity of the coloring it's meant to complement. Compared to Cool Winter, True Winter can take more saturation without looking overdone; compared to Deep Winter, True Winter's strength is brightness and contrast rather than sheer depth. If bold, clear colors make you look more awake rather than harsh, True Winter is likely your home base.",
    selfCheck: {
      skin: [
        "Cool undertone with high clarity — often porcelain, olive-cool, or deep cool without any warmth",
        "Strong contrast between skin tone and hair or eye color",
        "Skin looks best next to fully saturated, high-clarity colors",
        "Tends to look washed out or flat next to muted or warm tones",
      ],
      hair: [
        "Deep, cool hair color — black, cool dark brown, or blue-black",
        "High contrast against the skin is typical",
        "No warmth, red, or gold in the natural color",
        "Grey tends to come in salt-and-pepper with a cool cast, not yellowing",
      ],
      eyes: [
        "Vivid, cool eye color — deep brown, cool blue, or clear grey",
        "High contrast between iris and white of the eye",
        "Often described as striking or piercing",
        "No warm gold flecks around the pupil",
      ],
      overall: [
        "The most dramatic, high-contrast coloring of the three Winter subseasons",
        "Comes alive in fully saturated, cool jewel tones and true black and white",
        "Can carry bold makeup and bright color that would overwhelm softer types",
        "Metallics read best in silver, chrome, or platinum",
      ],
    },
    avoidDetails: [
      {
        name: "Cream",
        hex: "#FFFDD0",
        reason: "Too soft and warm a neutral for a palette that thrives on stark contrast.",
        swapFor: "Bright White",
        swapHex: "#FFFFFF",
      },
      {
        name: "Warm Camel",
        hex: "#C19A6B",
        reason: "The golden undertone clashes with True Winter's pure cool coloring.",
        swapFor: "Cool Grey",
        swapHex: "#A9A9A9",
      },
      {
        name: "Rust Orange",
        hex: "#B7410E",
        reason: "A warm, muted tone that has no counterpart in True Winter's clear, cool palette.",
        swapFor: "Ruby",
        swapHex: "#E0115F",
      },
      {
        name: "Warm Gold",
        hex: "#FFD700",
        reason: "Too warm and soft next to True Winter's stark, high-contrast coloring.",
        swapFor: "Silver",
        swapHex: "#C0C0C0",
      },
      {
        name: "Terracotta",
        hex: "#E2725B",
        reason: "An earthy orange-red that dulls True Winter's vivid, cool-based reds.",
        swapFor: "Ruby",
        swapHex: "#E0115F",
      },
      {
        name: "Golden Brown",
        hex: "#996515",
        reason: "Too muted and warm to hold up against True Winter's demand for clarity and contrast.",
        swapFor: "Graphite",
        swapHex: "#1C1C1C",
      },
    ],
    comparisons: [
      {
        vsLabel: "Cool Winter",
        vsHref: "/color-palette/winter/cool",
        rows: [
          { attribute: "Undertone", thisSeason: "Cool, maximum clarity", otherSeason: "Cool, slightly softer" },
          { attribute: "Depth", thisSeason: "High contrast, vivid", otherSeason: "Light to medium, icy" },
          { attribute: "Best pink", thisSeason: "Hot pink / fuchsia", otherSeason: "Icy pink", thisHex: "#FF69B4", otherHex: "#FFDEE9" },
          {
            attribute: "Best blue/red",
            thisSeason: "Electric blue, ruby",
            otherSeason: "Ice blue, berry",
            thisHex: "#0055FF",
            otherHex: "#A5F2F3",
          },
          {
            attribute: "Metals",
            thisSeason: "Silver, bright white gold",
            otherSeason: "Silver, platinum",
            thisHex: "#E8E8E8",
            otherHex: "#C0C0C0",
          },
        ],
      },
      {
        vsLabel: "Deep Winter",
        vsHref: "/color-palette/winter/deep",
        rows: [
          { attribute: "Undertone", thisSeason: "Cool, pure", otherSeason: "Cool, pure" },
          { attribute: "Depth", thisSeason: "High contrast, bright", otherSeason: "Deep, rich, low-to-mid contrast" },
          {
            attribute: "Best pink",
            thisSeason: "Hot pink / fuchsia",
            otherSeason: "Deep magenta",
            thisHex: "#FF69B4",
            otherHex: "#8B008B",
          },
          {
            attribute: "Best blue/red",
            thisSeason: "Electric blue, ruby",
            otherSeason: "Deep sapphire, deep ruby",
            thisHex: "#0055FF",
            otherHex: "#082567",
          },
          {
            attribute: "Metals",
            thisSeason: "Silver, bright white gold",
            otherSeason: "Silver, gunmetal",
            thisHex: "#E8E8E8",
            otherHex: "#8A8A90",
          },
        ],
      },
    ],
    capsule: [
      { item: "Wool coat", color: "Black", hex: "#000000" },
      { item: "Blazer", color: "Graphite", hex: "#1C1C1C" },
      { item: "Blouse", color: "Frost", hex: "#E5F9F9" },
      { item: "Trousers", color: "Black", hex: "#000000" },
      { item: "Sweater", color: "Cyan", hex: "#00FFFF" },
      { item: "Dress", color: "Ruby", hex: "#E0115F" },
      { item: "Cardigan", color: "Cool Grey", hex: "#A9A9A9" },
      { item: "Shoes", color: "Bright White", hex: "#FFFFFF" },
      { item: "Bag", color: "Electric Blue", hex: "#0055FF" },
      { item: "Denim", color: "Ultramarine", hex: "#3F00FF" },
    ],
    celebrities: [
      {
        name: "Zooey Deschanel",
        why: "Blue-black hair against porcelain skin and vivid blue eyes is a defining True Winter combination.",
        imageUrl:
          "https://upload.wikimedia.org/wikipedia/commons/thumb/0/00/Zooey_Deschanel_-_Song_Sung_Blue.jpg/330px-Zooey_Deschanel_-_Song_Sung_Blue.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Zooey_Deschanel",
      },
      {
        name: "Krysten Ritter",
        why: "Near-black hair, pale cool skin, and striking eyes give her the stark, high-contrast look of True Winter.",
        imageUrl:
          "https://upload.wikimedia.org/wikipedia/commons/thumb/c/c4/Krysten_Ritter_-_LA_Times_Festival_of_Books_2025.jpg/330px-Krysten_Ritter_-_LA_Times_Festival_of_Books_2025.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Krysten_Ritter",
      },
      {
        name: "Alexis Bledel",
        why: "Very fair, cool skin against dark hair and bright blue eyes creates dramatic True Winter contrast.",
        imageUrl:
          "https://upload.wikimedia.org/wikipedia/commons/thumb/8/8d/AlexisBledelSept11TIFF.jpg/330px-AlexisBledelSept11TIFF.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Alexis_Bledel",
      },
      {
        name: "Dita Von Teese",
        why: "Jet black hair, porcelain skin, and bold red lips embody True Winter's love of stark, saturated contrast.",
        imageUrl:
          "https://upload.wikimedia.org/wikipedia/commons/thumb/a/a8/AV0A1522_Dita_Von_Teese.jpg/330px-AV0A1522_Dita_Von_Teese.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Dita_Von_Teese",
      },
      {
        name: "Rooney Mara",
        why: "Dark cool hair and fair, clear skin give her the crisp, high-contrast coloring True Winter is known for.",
        imageUrl:
          "https://upload.wikimedia.org/wikipedia/commons/thumb/c/c6/Rooney_Mara_at_Berlinale_2024-3.jpg/330px-Rooney_Mara_at_Berlinale_2024-3.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Rooney_Mara",
      },
      {
        name: "Audrey Hepburn",
        why: "Dark hair, fair cool skin, and deep expressive eyes made her a timeless True Winter icon.",
        imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/74/AudreyKHepburn.jpg/330px-AudreyKHepburn.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Audrey_Hepburn",
      },
    ],
    extraFaqs: [
      {
        question: "Is True Winter different from Cool Winter?",
        answer:
          "Yes. Both are cool-undertoned, but True Winter has more contrast and can wear more saturated, brighter colors. Cool Winter is a step softer and icier. If vivid fuchsia and electric blue outshine icy pastels on you, True Winter is the better fit.",
      },
      {
        question: "How does True Winter compare to Deep Winter?",
        answer:
          "True Winter is defined by contrast and brightness; Deep Winter is defined by depth. True Winter wears vivid, high-clarity color best, while Deep Winter looks best in rich, dark shades even if they're less bright. Both share the same pure cool undertone.",
      },
      {
        question: "What jewelry metals suit True Winter?",
        answer:
          "Silver, platinum, and bright white gold. These sharp, cool metals match the intensity of your coloring. Warm yellow gold tends to look dull next to True Winter's high contrast.",
      },
      {
        question: "What hair colors suit True Winter?",
        answer:
          "Cool black, blue-black, and deep cool brown are the most flattering. Avoid warm brunette shades with red or gold undertones — they mute the contrast that makes True Winter coloring striking.",
      },
      {
        question: "How do I know if I'm True Winter and not Deep Winter?",
        answer:
          "Try a bright saturated color like true red or emerald next to a deep, dark version of the same color. If the brighter version makes you look more alive, you're likely True Winter. If the deeper, richer shade suits you better, look at Deep Winter instead.",
      },
    ],
    vibe: {
      skinTones: ["#F3E1DB", "#DCB9AE", "#B98B7E", "#7A5246"],
      hairTones: ["#0A0A0A", "#1C2841", "#2A1A18", "#F0F0F0"],
      eyeColors: ["#2B1B12", "#7FA8C9", "#4A4A52", "#1A1A1A"],
    },
    hairToneSwatches: [
      { name: "Jet Black", hexes: ["#000000", "#0A0A0A", "#2A2A2A"] },
      { name: "Blue-Black", hexes: ["#0A0F1A", "#1C2841", "#4A5A78"] },
      { name: "Espresso", hexes: ["#140C0B", "#2A1A18", "#523C38"] },
      { name: "Icy White", hexes: ["#C8C8C8", "#F0F0F0", "#FFFFFF"] },
      { name: "Cool Dark Brown", hexes: ["#161311", "#2A2420", "#544C44"] },
      { name: "Silver", hexes: ["#787880", "#B0B0B8", "#E0E0E4"] },
      { name: "Platinum Blonde", hexes: ["#B0AAB0", "#E8E4E8", "#FAF8FA"] },
    ],
    metals: [
      { name: "Bright Silver", hexes: ["#AEAEAE", "#E8E8E8", "#FBFBFB"] },
      { name: "Platinum", hexes: ["#ABA9A6", "#E5E4E2", "#FAFAF9"] },
      { name: "White Gold", hexes: ["#AEAAA5", "#E8E4E1", "#FBFAF8"] },
    ],
  },
  DEEP: {
    intro:
      "Deep Winter is the richest, most saturated end of the Winter family — cool undertones combined with real depth rather than the icy lightness of Cool Winter or the maximum brightness of True Winter. Skin, hair, and eyes tend to run darker overall, and the contrast between them is often more about density than stark black-and-white brightness. A deep winter color palette leans on colors that look like they've been dipped in ink: deep sapphire, aubergine, deep emerald, oxblood — jewel tones with weight, not pastel or muted versions of the same hue. Positioned between True Winter's high-contrast brightness and Deep Autumn's warm richness, Deep Winter keeps the cool undertone but trades Winter's usual sharpness for something more grounded and dramatic. This is the Winter subseason that can wear the darkest, most saturated shades in the entire seasonal wheel without looking flat, because the depth of the palette matches the depth of the coloring it's built for.",
    selfCheck: {
      skin: [
        "Cool or neutral-cool undertone with noticeable depth — olive-cool, deep tan, or rich deep skin",
        "Skin can look sallow or ashy next to pale, icy colors that suit lighter Winters",
        "Comes alive next to deep, rich jewel tones rather than pastels",
        "Often has a smooth, dense quality rather than a translucent one",
      ],
      hair: [
        "Deep, cool hair color — black, deep brown, or near-black with cool undertones",
        "Little to no red, gold, or warm caramel in the natural shade",
        "Contrast with skin tends to be moderate rather than stark, since both run deep",
        "Grey often comes in as a cool salt-and-pepper rather than silvery-icy",
      ],
      eyes: [
        "Deep, cool eye color — dark brown, deep hazel, or deep cool blue/green",
        "Color often looks even richer in low light",
        "Little warmth or gold in the iris",
        "Contrast between iris and pupil can be subtle since both are dark",
      ],
      overall: [
        "Reads as rich and dramatic rather than icy or ultra-bright",
        "Can wear the darkest, most saturated shades in the Winter wheel without disappearing",
        "Muted, warm, or pastel colors tend to wash out the natural depth",
        "Metallics read best in silver, gunmetal, or platinum",
      ],
    },
    avoidDetails: [
      {
        name: "Warm Ivory",
        hex: "#FFFFF0",
        reason: "Too pale and warm to hold up against Deep Winter's natural depth.",
        swapFor: "Pewter Grey",
        swapHex: "#899499",
      },
      {
        name: "Warm Camel",
        hex: "#C19A6B",
        reason: "The golden undertone clashes with Deep Winter's cool, rich coloring.",
        swapFor: "Espresso",
        swapHex: "#3C2A21",
      },
      {
        name: "Terracotta",
        hex: "#E2725B",
        reason: "An earthy warm tone that fights Deep Winter's cool jewel-toned palette.",
        swapFor: "Deep Ruby",
        swapHex: "#9B111E",
      },
      {
        name: "Warm Moss",
        hex: "#8A9A5B",
        reason: "Too muted and warm; Deep Winter needs its greens cool and richly saturated instead.",
        swapFor: "Deep Emerald",
        swapHex: "#005F5F",
      },
      {
        name: "Golden Brown",
        hex: "#996515",
        reason: "A warm, dusty brown that flattens Deep Winter's natural richness instead of matching it.",
        swapFor: "Deep Charcoal",
        swapHex: "#2B2B2B",
      },
      {
        name: "Camel",
        hex: "#C19A6B",
        reason: "Too soft and warm a neutral for a palette built on cool, saturated depth.",
        swapFor: "Deep Slate",
        swapHex: "#2F4F4F",
      },
    ],
    comparisons: [
      {
        vsLabel: "True Winter",
        vsHref: "/color-palette/winter/true",
        rows: [
          { attribute: "Undertone", thisSeason: "Cool, pure", otherSeason: "Cool, pure" },
          { attribute: "Depth", thisSeason: "Deep, rich, low-to-mid contrast", otherSeason: "High contrast, bright" },
          {
            attribute: "Best pink",
            thisSeason: "Deep magenta",
            otherSeason: "Hot pink / fuchsia",
            thisHex: "#8B008B",
            otherHex: "#FF69B4",
          },
          {
            attribute: "Best blue/red",
            thisSeason: "Deep sapphire, deep ruby",
            otherSeason: "Electric blue, ruby",
            thisHex: "#082567",
            otherHex: "#0055FF",
          },
          {
            attribute: "Metals",
            thisSeason: "Silver, gunmetal",
            otherSeason: "Silver, bright white gold",
            thisHex: "#8A8A90",
            otherHex: "#E8E8E8",
          },
        ],
      },
      {
        vsLabel: "Deep Autumn",
        vsHref: "/color-palette/autumn/deep",
        rows: [
          { attribute: "Undertone", thisSeason: "Cool", otherSeason: "Warm" },
          { attribute: "Depth", thisSeason: "Deep, rich", otherSeason: "Deep, rich" },
          {
            attribute: "Best pink",
            thisSeason: "Deep magenta",
            otherSeason: "Brick / terracotta pink",
            thisHex: "#8B008B",
            otherHex: "#E2725B",
          },
          {
            attribute: "Best blue/red",
            thisSeason: "Deep sapphire, deep ruby",
            otherSeason: "Rust red, warm teal",
            thisHex: "#082567",
            otherHex: "#B7410E",
          },
          {
            attribute: "Metals",
            thisSeason: "Silver, gunmetal",
            otherSeason: "Gold, bronze",
            thisHex: "#8A8A90",
            otherHex: "#D4A43A",
          },
        ],
      },
    ],
    capsule: [
      { item: "Wool coat", color: "Deep Charcoal", hex: "#2B2B2B" },
      { item: "Blazer", color: "Deep Navy", hex: "#000080" },
      { item: "Blouse", color: "Frost", hex: "#E5F9F9" },
      { item: "Trousers", color: "Black", hex: "#000000" },
      { item: "Sweater", color: "Deep Teal", hex: "#004B49" },
      { item: "Dress", color: "Aubergine", hex: "#580F41" },
      { item: "Cardigan", color: "Deep Slate", hex: "#2F4F4F" },
      { item: "Shoes", color: "Deep Charcoal", hex: "#2B2B2B" },
      { item: "Bag", color: "Deep Sapphire", hex: "#082567" },
      { item: "Denim", color: "Midnight Green", hex: "#004953" },
    ],
    celebrities: [
      {
        name: "Katy Perry",
        why: "Very dark hair and deep contrast against striking features give her the rich intensity of Deep Winter.",
        imageUrl:
          "https://upload.wikimedia.org/wikipedia/commons/thumb/e/ef/KatyPerryWestminst111224_%2881_of_95%29_%2854206733094%29_%28cropped_2%29.jpg/330px-KatyPerryWestminst111224_%2881_of_95%29_%2854206733094%29_%28cropped_2%29.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Katy_Perry",
      },
      {
        name: "Liv Tyler",
        why: "Deep brown hair and rich, cool-toned coloring give her the grounded depth typical of this subseason.",
        imageUrl:
          "https://upload.wikimedia.org/wikipedia/commons/thumb/f/f8/Liv_Tyler_%282016%29.jpg/330px-Liv_Tyler_%282016%29.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Liv_Tyler",
      },
      {
        name: "Angelina Jolie",
        why: "Deep, cool-toned hair and richly pigmented features embody Deep Winter's dense, dramatic coloring.",
        imageUrl:
          "https://upload.wikimedia.org/wikipedia/commons/thumb/1/12/Angelina_Jolie-643531_%28cropped%29.jpg/330px-Angelina_Jolie-643531_%28cropped%29.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Angelina_Jolie",
      },
      {
        name: "Emilia Clarke",
        why: "Deep dark hair paired with cool undertoned skin gives her the rich depth Deep Winter is known for.",
        imageUrl:
          "https://upload.wikimedia.org/wikipedia/commons/thumb/2/23/Emilia_Clarke_at_the_2023_Harper%27s_Bazaar_Women_of_the_Year_Awards.jpg/330px-Emilia_Clarke_at_the_2023_Harper%27s_Bazaar_Women_of_the_Year_Awards.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Emilia_Clarke",
      },
      {
        name: "Kendall Jenner",
        why: "Deep brunette hair and cool, richly saturated coloring place her firmly in the Deep Winter range.",
        imageUrl: "https://upload.wikimedia.org/wikipedia/commons/5/54/Kendall_Jenner_for_Adanola_2_%28cropped%29.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Kendall_Jenner",
      },
      {
        name: "Megan Boone",
        why: "Dark, cool-based hair and deep, rich coloring give her the grounded intensity of Deep Winter.",
        imageUrl:
          "https://upload.wikimedia.org/wikipedia/commons/thumb/f/fc/The_Blacklist_-_Megan_Boone_%28cropped%29.jpg/330px-The_Blacklist_-_Megan_Boone_%28cropped%29.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Megan_Boone",
      },
    ],
    extraFaqs: [
      {
        question: "Can Deep Winter wear black?",
        answer:
          "Yes — Deep Winter wears black exceptionally well, often better than any other subseason, since black matches its natural depth. Deep charcoal and deep navy work just as well when you want a slightly softer alternative near the face.",
      },
      {
        question: "How is Deep Winter different from True Winter?",
        answer:
          "Both are cool, but True Winter is about brightness and contrast, while Deep Winter is about depth. True Winter lights up in vivid, saturated brights; Deep Winter looks best in dark, rich jewel tones even when they're less bright.",
      },
      {
        question: "How does Deep Winter compare to Deep Autumn?",
        answer:
          "They share depth, but the undertone differs. Deep Winter is cool — think deep sapphire and cool ruby. Deep Autumn is warm — think rust and bronze. If cool jewel tones flatter you more than warm earthy ones, you're Deep Winter, not Deep Autumn.",
      },
      {
        question: "What jewelry metals suit Deep Winter?",
        answer:
          "Silver, platinum, and gunmetal are the strongest choices. White gold also works. Yellow gold and copper tend to clash with Deep Winter's cool undertone, even though the overall look is rich and dramatic.",
      },
      {
        question: "What hair colors work for Deep Winter?",
        answer:
          "Deep black, cool dark brown, and near-black shades are most natural. If coloring your hair, stick to cool, deep formulas rather than warm chestnut or caramel, which fight the cool undertone.",
      },
      {
        question: "How do I confirm I'm Deep Winter and not True Winter?",
        answer:
          "Compare a deep, dark jewel tone like aubergine to a bright, saturated version like bright purple. If the deep, dark shade makes you look richer and more polished, you're Deep Winter. If the brighter version is more flattering, True Winter is the better match.",
      },
    ],
    vibe: {
      skinTones: ["#C99A82", "#A9765E", "#7A4E3A", "#4A2E20"],
      hairTones: ["#1A1008", "#1C2030", "#0A0A0A", "#2A2018"],
      eyeColors: ["#150D08", "#2A2020", "#3A2E28", "#1C1C24"],
    },
    hairToneSwatches: [
      { name: "Deep Espresso", hexes: ["#0D0804", "#1A1008", "#3A2C18"] },
      { name: "Blue-Black", hexes: ["#0A0C16", "#1C2030", "#3A4258"] },
      { name: "Rich Black", hexes: ["#000000", "#0A0A0A", "#2A2A2A"] },
      { name: "Deep Cool Brown", hexes: ["#140F0A", "#2A2018", "#544636"] },
      { name: "Dark Burgundy", hexes: ["#280F18", "#4A1A2A", "#7A3450"] },
      { name: "Deep Plum Brown", hexes: ["#1E1218", "#3A2028", "#6A3C4A"] },
      { name: "Cool Black", hexes: ["#000000", "#0A0A10", "#2A2A34"] },
    ],
    metals: [
      { name: "Silver", hexes: ["#8A8F94", "#C0C0C0", "#EDEDED"] },
      { name: "Gunmetal", hexes: ["#5E5E64", "#8A8A90", "#B8B8BE"] },
      { name: "Dark Platinum", hexes: ["#9C9C9C", "#D0D0D0", "#F0F0F0"] },
    ],
  },
};
